import { api } from 'api';
import { PaginationParams, PaginatedResponse } from 'common/state/types';
import { confirmInDangerousDev } from 'common/utils/dev';
import { requestErrorAction } from 'common/utils/requestErrorHandling';
import {
  ClientTransfer,
  ClientTransferRequest,
  ClientTransferRequestStatuses,
  ClientTransferStatuses,
} from './actions';

// #region ClientTransfers
export interface NewClientTransferBodyData {
  amount: string;
  currency: string;
  date: string;
  description: string;
  destination: string;
  reference: string;
  sender: string;
  request?: string;
}

export const requestNewClientTransfer = async (
  data: NewClientTransferBodyData
) =>
  confirmInDangerousDev(
    async () => await api.post<ClientTransfer>('/transfer/', data)
  );

export const doNewClientTransfer =
  (data: NewClientTransferBodyData) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/new/started' });

    try {
      await requestNewClientTransfer(data);

      dispatch({
        type: 'clients/transfers/new/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfers/new/finished', success: false },
          e,
          `Failed to make new client transfer.`
        )
      );
    }
  };

export interface AcceptClientTransferBodyData {
  got_amount: string;
  got_currency: string;
  bank_reference: string;
  bank_date: string;
}

export const requestAcceptClientTransfer = async (
  url: string,
  data: AcceptClientTransferBodyData
) =>
  confirmInDangerousDev(
    async () => {
      await api.patch<ClientTransfer>(url, {
        ...data,
        status: ClientTransferStatuses.ACCEPTED,
      })
    }
  );

export const doAcceptClientTransfer =
  (url: string, data: AcceptClientTransferBodyData) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/accept/started' });

    try {
      await requestAcceptClientTransfer(url, data);

      dispatch({
        type: 'clients/transfers/accept/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfers/accept/finished', success: false },
          e,
          `Failed to make accept client transfer.`
        )
      );
    }
  };

export interface UnacceptClientTransferBodyData {
  got_amount: string;
  got_currency: string;
}

export const requestUnacceptClientTransfer = async (
  url: string,
  data: UnacceptClientTransferBodyData
) =>
  confirmInDangerousDev(
    async () =>
      await api.patch<ClientTransfer>(url, {
        ...data,
        status: ClientTransferStatuses.UNACCEPTED,
      })
  );

export const doUnacceptClientTransfer =
  (url: string, data: UnacceptClientTransferBodyData) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/unaccept/started' });

    try {
      await requestUnacceptClientTransfer(url, data);

      dispatch({
        type: 'clients/transfers/unaccept/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfers/unaccept/finished', success: false },
          e,
          `Failed to make unaccept client transfer.`
        )
      );
    }
  };

export interface ClientIncomingTransfersParams extends PaginationParams {
  status?: ClientTransferStatuses;
  start_date?: string;
  end_date?: string;
}

export const requestClientIncomingTransfers = async (
  params?: ClientIncomingTransfersParams
) =>
  await api.get<PaginatedResponse<ClientTransfer>>('/transfer/incoming/', {
    params,
  });

export const doGetClientIncomingTransfers =
  (params?: ClientIncomingTransfersParams) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/incoming/started' });

    try {
      const response = await requestClientIncomingTransfers(params);
      dispatch({
        type: 'clients/transfers/incoming/finished',
        transfers: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfers/incoming/finished' },
          e,
          `Failed to get incoming transfers.`
        )
      );
    }
  };

export const requestUserIncomingTransfers = async (
  user: string | number,
  params?: ClientIncomingTransfersParams
) =>
  await api.get<PaginatedResponse<ClientTransfer>>(
    `/contact/${user}/transfer/incoming/`,
    {
      params,
    }
  );

export const doGetUserIncomingTransfers =
  (user: string | number, params?: ClientIncomingTransfersParams) =>
  async (dispatch) => {
    dispatch({ type: 'users/transfers/incoming/started' });

    try {
      const response = await requestUserIncomingTransfers(user, params);
      dispatch({
        type: 'contacts/transfers/incoming/finished',
        transfers: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'contacts/transfers/incoming/finished' },
          e,
          `Failed to get incoming transfers.`
        )
      );
    }
  };

export interface ClientOutgoingTransfersParams extends PaginationParams {
  status?: ClientTransferStatuses;
  start_date?: string;
  end_date?: string;
}

export const requestClientOutgoingTransfers = async (
  params?: ClientOutgoingTransfersParams
) =>
  await api.get<PaginatedResponse<ClientTransfer>>('/transfer/outgoing/', {
    params,
  });

export const doGetClientOutgoingTransfers =
  (params?: ClientOutgoingTransfersParams) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/outgoing/started' });

    try {
      const response = await requestClientOutgoingTransfers(params);

      dispatch({
        type: 'clients/transfers/outgoing/finished',
        transfers: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfers/outgoing/finished' },
          e,
          `Failed to get outgoing transfers.`
        )
      );
    }
  };

export const requestUserOutgoingTransfers = async (
  user: string | number,
  params?: ClientOutgoingTransfersParams
) =>
  await api.get<PaginatedResponse<ClientTransfer>>(
    `/contact/${user}/transfer/outgoing/`,
    {
      params,
    }
  );

export const doGetUserOutgoingTransfers =
  (user: string | number, params?: ClientOutgoingTransfersParams) =>
  async (dispatch) => {
    dispatch({ type: 'contacts/transfers/outgoing/started' });

    try {
      const response = await requestUserOutgoingTransfers(user, params);

      dispatch({
        type: 'contacts/transfers/outgoing/finished',
        transfers: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'contacts/transfers/outgoing/finished' },
          e,
          `Failed to get outgoing transfers.`
        )
      );
    }
  };
// #endregion

// #region ClientTransferRequests
export interface NewClientTransferRequestBodyData {
  amount: string;
  currency: string;
  date: string;
  description: string;
  receiver: string;
  reference: string;
  sender: string;
}

export const requestNewClientTransferRequest = async (
  data: NewClientTransferRequestBodyData
) =>
  confirmInDangerousDev(
    async () =>
      await api.post<ClientTransferRequest>('/transfer_request/', data)
  );

export const doNewClientTransferRequest =
  (data: NewClientTransferRequestBodyData) => async (dispatch) => {
    dispatch({ type: 'clients/transfer_requests/new/started' });

    try {
      await requestNewClientTransferRequest(data);

      dispatch({
        type: 'clients/transfer_requests/new/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfer_requests/new/finished', success: false },
          e,
          `Failed to make new client transfer request.`
        )
      );
    }
  };

export const requestCompleteClientTransferRequest = async (url: string) =>
  confirmInDangerousDev(
    async () =>
      await api.patch<ClientTransferRequest>(url, {
        status: ClientTransferRequestStatuses.COMPLETED,
      })
  );

export const doCompleteClientTransferRequest =
  (url: string) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/complete/started' });

    try {
      await requestCompleteClientTransferRequest(url);

      dispatch({
        type: 'clients/transfer_requests/complete/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          {
            type: 'clients/transfer_requests/complete/finished',
            success: false,
          },
          e,
          `Failed to complete client transfer request.`
        )
      );
    }
  };

export const requestCancelClientTransferRequest = async (url: string) =>
  confirmInDangerousDev(
    async () =>
      await api.patch<ClientTransferRequest>(url, {
        status: ClientTransferRequestStatuses.CANCELLED,
      })
  );

export const doCancelClientTransferRequest =
  (url: string) => async (dispatch) => {
    dispatch({ type: 'clients/transfers/cancel/started' });

    try {
      await requestCancelClientTransferRequest(url);

      dispatch({
        type: 'clients/transfer_requests/cancel/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfer_requests/cancel/finished', success: false },
          e,
          `Failed to cancel client transfer request.`
        )
      );
    }
  };

export interface ClientTransferRequestsParams extends PaginationParams {
  status?: ClientTransferStatuses;
  start_date?: string;
  end_date?: string;
}

export const requestClientTransferRequests = async (
  params?: ClientTransferRequestsParams
) =>
  await api.get<PaginatedResponse<ClientTransferRequest>>(
    '/transfer_request/',
    {
      params,
    }
  );

export const doGetClientTransferRequests =
  (params?: ClientTransferRequestsParams) => async (dispatch) => {
    dispatch({ type: 'clients/transfer_requests/started' });

    try {
      const response = await requestClientTransferRequests(params);

      dispatch({
        type: 'clients/transfer_requests/finished',
        requests: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/transfer_requests/finished' },
          e,
          `Failed to get client transfer requests.`
        )
      );
    }
  };
// #endregion
